import { HTMLAttributes, forwardRef, ReactNode } from 'react';

export type BadgeVariant = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent';
export type BadgeSize = 'sm' | 'md';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  leftIcon?: ReactNode;
}

const BASE = 'inline-flex items-center rounded-badge font-medium whitespace-nowrap';

const VARIANT_CLASSES: Record<BadgeVariant, string> = {
  neutral: 'bg-surface-muted text-text-secondary border border-border',
  success: 'bg-success-bg text-success border border-success-border',
  warning: 'bg-warning-bg text-warning border border-warning-border',
  danger:  'bg-danger-bg text-danger border border-danger-border',
  info:    'bg-info-bg text-info border border-info-border',
  accent:  'bg-accent-primary/15 text-accent-primary border border-accent-primary/30',
};

const SIZE_CLASSES: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xxs gap-1',
  md: 'px-2.5 py-1 text-xs gap-1.5',
};

const ICON_SIZE: Record<BadgeSize, string> = {
  sm: 'w-3 h-3',
  md: 'w-3.5 h-3.5',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { variant = 'neutral', size = 'md', leftIcon, className, children, ...rest },
  ref,
) {
  return (
    <span
      ref={ref}
      className={[BASE, VARIANT_CLASSES[variant], SIZE_CLASSES[size], className].filter(Boolean).join(' ')}
      {...rest}
    >
      {leftIcon && (
        <span
          className={`inline-flex items-center justify-center shrink-0 ${ICON_SIZE[size]} [&>svg]:w-full [&>svg]:h-full`}
          aria-hidden="true"
        >
          {leftIcon}
        </span>
      )}
      {children}
    </span>
  );
});
